import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { clientApi, paymentApi } from '../lib/api';
import { formatDate, formatPrice, RESERVATION_STATUS, PAYMENT_STATUS } from '../lib/utils';
import { Calendar, ShoppingCart, FileText, ChevronRight, Plus, LogOut, User, Download, Clock, MapPin, Car, AlertTriangle, X, RefreshCw, ChefHat, Check } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { SelectedOptionsSummary } from '../components/SelectedOptionsSummary';
import { toast } from 'sonner';
import Logo from '../components/Logo';

export default function ClientDashboard() {
  const { user, logout } = useAuth();
  const { config } = useConfig();
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kitchenStatus, setKitchenStatus] = useState(null);
  const [showKitchenModal, setShowKitchenModal] = useState(false);
  const [kitchenEquipment, setKitchenEquipment] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [savingKitchen, setSavingKitchen] = useState(false);
  const [zoneStatus, setZoneStatus] = useState(null);
  const [showWaitingListModal, setShowWaitingListModal] = useState(false);
  const [registeringWaitingList, setRegisteringWaitingList] = useState(false);

  useEffect(() => {
    loadReservations();
    loadKitchenStatus();
    loadZoneStatus();
    
    // Check for payment return
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, []);

  const loadZoneStatus = async () => {
    try {
      const response = await clientApi.getZoneStatus();
      setZoneStatus(response.data);
      
      // If out of zone, show waiting list modal
      if (!response.data.in_zone) {
        setShowWaitingListModal(true);
      }
    } catch (error) {
      console.error('Error loading zone status:', error);
    }
  };

  const handleRegisterWaitingList = async () => {
    setRegisteringWaitingList(true);
    try {
      const response = await clientApi.registerWaitingList({
        postal_code: user?.postal_code || '',
        city: user?.city || '',
        email: user?.email || '',
        name: user?.name || ''
      });
      toast.success(response.data.message);
      setShowWaitingListModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setRegisteringWaitingList(false);
    }
  };

  const loadKitchenStatus = async () => {
    try {
      const response = await clientApi.getKitchenProfile();
      setKitchenStatus(response.data);
      
      // Auto-show modal if needs completion or address changed
      if (response.data.enabled && ['needs_completion', 'address_changed', 'expired'].includes(response.data.status)) {
        loadKitchenEquipment();
        if (response.data.profile) {
          setSelectedEquipment(response.data.profile.equipment_ids || []);
        }
        setShowKitchenModal(true);
      }
    } catch (error) {
      console.error('Error loading kitchen status:', error);
    }
  };

  const loadKitchenEquipment = async () => {
    try {
      const response = await clientApi.getKitchenEquipmentList();
      setKitchenEquipment(response.data.equipment || []);
    } catch (error) {
      console.error('Error loading equipment:', error);
    }
  };

  const handleSaveKitchen = async () => {
    // Point 3: Vérifier les équipements obligatoires avant d'envoyer
    const requiredIds = kitchenEquipment.filter(eq => eq.is_required).map(eq => eq.equipment_id);
    const missingRequired = requiredIds.filter(id => !selectedEquipment.includes(id));
    
    if (missingRequired.length > 0) {
      const missingNames = kitchenEquipment
        .filter(eq => missingRequired.includes(eq.equipment_id))
        .map(eq => eq.name);
      toast.error(`Équipements obligatoires manquants : ${missingNames.join(', ')}`);
      return;
    }
    
    setSavingKitchen(true);
    try {
      const response = await clientApi.saveKitchenProfile({ equipment_ids: selectedEquipment });
      if (response.data.is_valid) {
        toast.success('Questionnaire enregistré !');
        setShowKitchenModal(false);
        loadKitchenStatus();
      } else {
        // Point 2: Afficher précisément les équipements manquants
        const missingList = response.data.missing_required?.map(eq => eq.name).join(', ') || '';
        toast.error(`Équipements obligatoires manquants : ${missingList}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSavingKitchen(false);
    }
  };

  const toggleEquipment = (equipmentId) => {
    setSelectedEquipment(prev => 
      prev.includes(equipmentId) 
        ? prev.filter(id => id !== equipmentId)
        : [...prev, equipmentId]
    );
  };

  const loadReservations = async () => {
    try {
      const response = await clientApi.getMyReservations();
      setReservations(response.data);
    } catch (error) {
      console.error('Error loading reservations:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.info('Vérification du paiement en cours...');
      return;
    }

    try {
      const response = await paymentApi.getCheckoutStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        toast.success('Paiement confirmé !');
        loadReservations();
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Payment status error:', error);
    }
  };

  const handlePayment = async (reservation) => {
    try {
      const response = await paymentApi.createCheckout(
        reservation.reservation_id,
        window.location.origin
      );
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Erreur lors de la création du paiement');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const upcomingReservations = reservations.filter(r => ['en_attente', 'confirmee'].includes(r.status));
  const pastReservations = reservations.filter(r => ['terminee', 'annulee'].includes(r.status));

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      {/* Header */}
      <header className="header-glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <Logo />
            </Link>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-[#5C6A61]">
                <User className="w-5 h-5" />
                <span className="hidden sm:inline">{user?.name}</span>
              </div>
              <button 
                onClick={handleLogout}
                className="text-[#5C6A61] hover:text-[#C86B51] transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Zone Alert - Out of zone */}
        {zoneStatus && !zoneStatus.in_zone && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3" data-testid="zone-alert">
            <MapPin className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-red-800">Zone non couverte</p>
              <p className="text-sm text-red-700">{zoneStatus.message}</p>
            </div>
            <button 
              onClick={() => setShowWaitingListModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm whitespace-nowrap"
            >
              M'inscrire
            </button>
          </div>
        )}

        {/* Zone Alert - In zone but no employee */}
        {zoneStatus && zoneStatus.in_zone && !zoneStatus.has_employee && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3" data-testid="zone-no-employee-alert">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-800">Aucun cuisinier disponible</p>
              <p className="text-sm text-amber-700">{zoneStatus.message}</p>
            </div>
          </div>
        )}

        {/* Kitchen Equipment Alert */}
        {kitchenStatus?.enabled && !kitchenStatus?.can_book && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3" data-testid="kitchen-alert">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-800">Questionnaire équipements requis</p>
              <p className="text-sm text-amber-700">{kitchenStatus.message}</p>
            </div>
            <button 
              onClick={() => { loadKitchenEquipment(); setShowKitchenModal(true); }}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm whitespace-nowrap"
            >
              Compléter
            </button>
          </div>
        )}

        {/* Welcome section */}
        <div className="mb-8">
          <h1 className="font-['Cormorant_Garamond'] text-3xl font-medium text-[#2A312C] mb-2">
            Bonjour {user?.name?.split(' ')[0]} !
          </h1>
          <p className="text-[#5C6A61]">Gérez vos réservations et consultez vos listes de courses</p>
        </div>

        {/* Quick actions */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Link 
            to="/client/reservation"
            className="card-default flex items-center gap-4 hover:-translate-y-1 transition-transform"
            data-testid="new-reservation-btn"
          >
            <div className="w-12 h-12 rounded-xl bg-[#C86B51]/10 flex items-center justify-center">
              <Plus className="w-6 h-6 text-[#C86B51]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#2A312C]">Nouvelle réservation</h3>
              <p className="text-sm text-[#5C6A61]">Planifiez votre prochaine session</p>
            </div>
            <ChevronRight className="w-5 h-5 text-[#5C6A61] ml-auto" />
          </Link>

          <Link 
            to="/client/profile"
            className="card-default flex items-center gap-4 hover:-translate-y-1 transition-transform"
            data-testid="profile-btn"
          >
            <div className="w-12 h-12 rounded-xl bg-[#A5BBA8]/20 flex items-center justify-center">
              <User className="w-6 h-6 text-[#A5BBA8]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#2A312C]">Mon profil</h3>
              <p className="text-sm text-[#5C6A61]">Modifier mes informations</p>
            </div>
            <ChevronRight className="w-5 h-5 text-[#5C6A61] ml-auto" />
          </Link>

          <Link 
            to="/client/payment"
            className="card-default flex items-center gap-4 hover:-translate-y-1 transition-transform"
            data-testid="payment-btn"
          >
            <div className="w-12 h-12 rounded-xl bg-[#5C6A61]/10 flex items-center justify-center">
              <FileText className="w-6 h-6 text-[#5C6A61]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#2A312C]">Paiement</h3>
              <p className="text-sm text-[#5C6A61]">Ma carte & historique</p>
            </div>
            <ChevronRight className="w-5 h-5 text-[#5C6A61] ml-auto" />
          </Link>

          <Link 
            to="/client/recurring"
            className="card-default flex items-center gap-4 hover:-translate-y-1 transition-transform"
            data-testid="recurring-btn"
          >
            <div className="w-12 h-12 rounded-xl bg-[#E8B25C]/10 flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-[#E8B25C]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#2A312C]">Créneau récurrent</h3>
              <p className="text-sm text-[#5C6A61]">Réservez chaque semaine</p>
            </div>
            <ChevronRight className="w-5 h-5 text-[#5C6A61] ml-auto" />
          </Link>

          <div className="card-default flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#E8B25C]/20 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-[#E8B25C]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#2A312C]">{upcomingReservations.length}</h3>
              <p className="text-sm text-[#5C6A61]">Réservation(s) à venir</p>
            </div>
          </div>
        </div>

        {/* Reservations */}
        <Tabs defaultValue="upcoming" className="w-full">
          <TabsList className="mb-6 bg-white border border-[#E3DED5]">
            <TabsTrigger value="upcoming" className="data-[state=active]:bg-[#A5BBA8] data-[state=active]:text-white">
              À venir ({upcomingReservations.length})
            </TabsTrigger>
            <TabsTrigger value="past" className="data-[state=active]:bg-[#A5BBA8] data-[state=active]:text-white">
              Historique ({pastReservations.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" data-testid="upcoming-reservations">
            {loading ? (
              <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>
            ) : upcomingReservations.length === 0 ? (
              <div className="card-default text-center py-12">
                <Calendar className="w-12 h-12 text-[#E3DED5] mx-auto mb-4" />
                <h3 className="font-semibold text-[#2A312C] mb-2">Aucune réservation à venir</h3>
                <p className="text-[#5C6A61] mb-4">Réservez votre première session de cuisine à domicile</p>
                <Link to="/client/reservation" className="btn-secondary inline-flex">
                  Réserver maintenant
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {upcomingReservations.map((reservation) => (
                  <ReservationCard 
                    key={reservation.reservation_id} 
                    reservation={reservation}
                    onPayment={handlePayment}
                    onDriveUpdate={loadReservations}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="past" data-testid="past-reservations">
            {pastReservations.length === 0 ? (
              <div className="card-default text-center py-12">
                <FileText className="w-12 h-12 text-[#E3DED5] mx-auto mb-4" />
                <h3 className="font-semibold text-[#2A312C] mb-2">Aucun historique</h3>
                <p className="text-[#5C6A61]">Vos réservations passées apparaîtront ici</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pastReservations.map((reservation) => (
                  <ReservationCard 
                    key={reservation.reservation_id} 
                    reservation={reservation}
                    isPast
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Kitchen Equipment Modal */}
      <Dialog open={showKitchenModal} onOpenChange={setShowKitchenModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ChefHat className="w-5 h-5 text-[#A5BBA8]" />
              Équipements de votre cuisine
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#5C6A61]">
              Cochez les équipements présents dans votre cuisine. Les équipements marqués d'un astérisque (*) sont obligatoires.
            </p>

            {kitchenStatus?.status === 'address_changed' && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-700">{kitchenStatus.message}</p>
              </div>
            )}

            <div className="space-y-3">
              {kitchenEquipment.map((item) => (
                <div 
                  key={item.equipment_id} 
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedEquipment.includes(item.equipment_id) 
                      ? 'border-[#A5BBA8] bg-[#A5BBA8]/10' 
                      : 'border-[#E3DED5] hover:border-[#A5BBA8]/50'
                  }`}
                  onClick={() => toggleEquipment(item.equipment_id)}
                  data-testid={`kitchen-equipment-${item.equipment_id}`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox 
                      checked={selectedEquipment.includes(item.equipment_id)}
                    />
                    <div className="flex-1">
                      <p className="font-medium text-[#2A312C]">
                        {item.name}
                        {item.is_required && <span className="text-[#C86B51] ml-1">*</span>}
                      </p>
                      {item.description && (
                        <p className="text-xs text-[#5C6A61]">{item.description}</p>
                      )}
                    </div>
                    {selectedEquipment.includes(item.equipment_id) && (
                      <Check className="w-5 h-5 text-[#A5BBA8]" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {kitchenStatus?.missing_required?.length > 0 && (
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="font-semibold text-red-800 text-sm mb-2">Équipements obligatoires manquants :</p>
                <ul className="text-sm text-red-700 space-y-1">
                  {kitchenStatus.missing_required.map((eq) => (
                    <li key={eq.equipment_id}>• {eq.name}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Affichage dynamique des équipements obligatoires non cochés */}
            {(() => {
              const requiredNotChecked = kitchenEquipment
                .filter(eq => eq.is_required && !selectedEquipment.includes(eq.equipment_id));
              if (requiredNotChecked.length > 0) {
                return (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="font-semibold text-amber-800 text-sm mb-2">
                      Équipements obligatoires non cochés ({requiredNotChecked.length}) :
                    </p>
                    <ul className="text-sm text-amber-700 space-y-1">
                      {requiredNotChecked.map((eq) => (
                        <li key={eq.equipment_id}>• {eq.name}</li>
                      ))}
                    </ul>
                  </div>
                );
              }
              return null;
            })()}

            <div className="flex gap-2 pt-4">
              <button 
                onClick={() => setShowKitchenModal(false)} 
                className="btn-secondary flex-1"
                data-testid="kitchen-close-btn"
              >
                Fermer
              </button>
              <button 
                onClick={handleSaveKitchen} 
                disabled={savingKitchen || kitchenEquipment.filter(eq => eq.is_required && !selectedEquipment.includes(eq.equipment_id)).length > 0}
                className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="kitchen-save-btn"
              >
                {savingKitchen ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Waiting List Modal - Out of Zone */}
      <Dialog open={showWaitingListModal} onOpenChange={setShowWaitingListModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#C86B51]" />
              Zone non couverte
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                {zoneStatus?.message || "Nous n'intervenons pas encore dans votre zone."}
              </p>
            </div>
            
            <p className="text-[#5C6A61]">
              Inscrivez-vous pour être informé(e) dès que Mitonner sera disponible dans votre secteur 
              ({user?.postal_code} - {user?.city}).
            </p>

            <div className="flex gap-2 pt-4">
              <button 
                onClick={() => setShowWaitingListModal(false)} 
                className="btn-secondary flex-1"
              >
                Plus tard
              </button>
              <button 
                onClick={handleRegisterWaitingList}
                disabled={registeringWaitingList}
                className="btn-primary flex-1"
                data-testid="waiting-list-register-btn"
              >
                {registeringWaitingList ? 'Inscription...' : 'M\'inscrire sur la liste'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReservationCard({ reservation, onPayment, isPast, onDriveUpdate }) {
  const statusInfo = RESERVATION_STATUS[reservation.status] || RESERVATION_STATUS.en_attente;
  const paymentInfo = PAYMENT_STATUS[reservation.payment_status] || PAYMENT_STATUS.en_attente;
  const [showDriveForm, setShowDriveForm] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelInfo, setCancelInfo] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [driveData, setDriveData] = useState({
    store_name: reservation.drive_info?.store_name || '',
    pickup_time: reservation.drive_info?.pickup_time || '',
    pickup_code: reservation.drive_info?.pickup_code || '',
    notes: reservation.drive_info?.notes || ''
  });
  const [saving, setSaving] = useState(false);

  const hasDriveOption = false;  // Option Drive supprimée : plus jamais affichée (historique conservé silencieusement)
  const driveIsFilled = reservation.drive_info?.is_filled;
  const showDriveAlert = hasDriveOption && !driveIsFilled && reservation.payment_status === 'paye' && !isPast;
  
  // Can cancel if not already cancelled or completed
  const canShowCancelButton = !isPast && reservation.status !== 'annulee' && reservation.status !== 'terminee';

  const handleCancelClick = async () => {
    try {
      const response = await clientApi.getCancellationInfo(reservation.reservation_id);
      setCancelInfo(response.data);
      setShowCancelDialog(true);
    } catch (error) {
      toast.error('Erreur lors de la vérification');
    }
  };

  const handleConfirmCancel = async () => {
    setCancelling(true);
    try {
      const response = await clientApi.cancelReservation(reservation.reservation_id, cancelReason);
      if (response.data.is_free_cancellation) {
        toast.success('Réservation annulée avec remboursement');
      } else {
        toast.info('Réservation annulée (sans remboursement)');
      }
      setShowCancelDialog(false);
      if (onDriveUpdate) onDriveUpdate(); // Refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    } finally {
      setCancelling(false);
    }
  };

  const handleSaveDrive = async () => {
    if (!driveData.store_name || !driveData.pickup_time || !driveData.pickup_code) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setSaving(true);
    try {
      await clientApi.updateDriveInfo(reservation.reservation_id, driveData);
      toast.success('Informations drive enregistrées !');
      setShowDriveForm(false);
      if (onDriveUpdate) onDriveUpdate();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Get slot display
  const getSlotDisplay = () => {
    if (reservation.slot_type === 'matin') return '08h00 - 12h00';
    if (reservation.slot_type === 'apres_midi') return '13h30 - 17h30';
    if (reservation.start_time) return reservation.start_time;
    return '-';
  };

  return (
    <div className="card-default" data-testid={`reservation-card-${reservation.reservation_id}`}>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#2A312C]">
              Réservation #{reservation.reservation_id.slice(-8)}
            </h3>
            <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-[#5C6A61]">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(reservation.date)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {getSlotDisplay()}
              {reservation.preferred_time && (
                <span className="text-xs text-[#5C6A61]">(préf: {reservation.preferred_time})</span>
              )}
            </span>
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {reservation.household_size} personne(s)
            </span>
          </div>
          <SelectedOptionsSummary selectedOptions={reservation.selected_options} className="mt-3" />
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="text-right">
            <p className="text-2xl font-semibold text-[#2A312C]">{formatPrice(reservation.total_amount)}</p>
            <Badge className={paymentInfo.color}>{paymentInfo.label}</Badge>
          </div>
          
          <div className="flex gap-2">
            {reservation.payment_status === 'en_attente' && !isPast && (
              <button 
                onClick={() => onPayment(reservation)}
                className="btn-secondary text-sm px-4 py-2"
                data-testid={`pay-btn-${reservation.reservation_id}`}
              >
                Payer
              </button>
            )}
            
            {reservation.payment_status === 'paye' && (
              <a 
                href={clientApi.downloadShoppingListPdf(reservation.reservation_id)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline text-sm px-4 py-2 inline-flex items-center gap-2"
                data-testid={`download-list-btn-${reservation.reservation_id}`}
              >
                <Download className="w-4 h-4" />
                Liste de courses
              </a>
            )}
            
            <Link 
              to={`/client/reservations/${reservation.reservation_id}`}
              className="btn-outline text-sm px-4 py-2"
            >
              Détails
            </Link>
            
            {canShowCancelButton && (
              <button 
                onClick={handleCancelClick}
                className="text-sm px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                data-testid={`cancel-btn-${reservation.reservation_id}`}
              >
                Annuler
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Cancellation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-xl">
              Annuler la réservation
            </DialogTitle>
          </DialogHeader>
          
          {cancelInfo && (
            <div className="space-y-4">
              {cancelInfo.is_free_cancellation ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="font-medium text-green-800">Annulation gratuite</p>
                  <p className="text-sm text-green-700">
                    Vous serez remboursé de {formatPrice(cancelInfo.amount_to_refund)}
                  </p>
                </div>
              ) : (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-800">Annulation tardive</p>
                      <p className="text-sm text-red-700">
                        La prestation reste due. Aucun remboursement ne sera effectué.
                      </p>
                      <p className="text-xs text-red-600 mt-2">
                        Il reste {cancelInfo.days_until_intervention} jour(s) avant l'intervention.
                        L'annulation gratuite est possible jusqu'à {cancelInfo.free_cancellation_deadline} jours avant.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <label className="text-sm text-[#5C6A61] block mb-1">Raison de l'annulation (optionnel)</label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="input-field"
                  rows={2}
                  placeholder="Indiquez la raison de votre annulation..."
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelDialog(false)}
                  className="btn-outline flex-1"
                >
                  Retour
                </button>
                <button
                  onClick={handleConfirmCancel}
                  disabled={cancelling}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {cancelling ? 'Annulation...' : 'Confirmer l\'annulation'}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Drive Alert & Form */}
      {hasDriveOption && (
        <div className="mt-4 pt-4 border-t border-[#E3DED5]">
          {showDriveAlert && (
            <div className="p-3 bg-[#E8B25C]/10 border border-[#E8B25C]/30 rounded-lg mb-3">
              <p className="text-sm font-medium text-[#E8B25C]">
                ⚠️ Informations Drive à renseigner
              </p>
              <p className="text-xs text-[#5C6A61] mt-1">
                Vous avez choisi l'option Drive. Veuillez renseigner les informations de récupération avant l'intervention.
              </p>
            </div>
          )}
          
          {driveIsFilled && !showDriveForm ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[#2A312C]">Drive: {reservation.drive_info.store_name}</p>
                <p className="text-xs text-[#5C6A61]">
                  Récupération à {reservation.drive_info.pickup_time} - Code: {reservation.drive_info.pickup_code}
                </p>
              </div>
              {!isPast && (
                <button 
                  onClick={() => setShowDriveForm(true)}
                  className="text-sm text-[#A5BBA8] hover:underline"
                >
                  Modifier
                </button>
              )}
            </div>
          ) : !isPast && (
            <>
              {!showDriveForm ? (
                <button 
                  onClick={() => setShowDriveForm(true)}
                  className="btn-outline text-sm w-full"
                  data-testid="fill-drive-btn"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Renseigner les informations Drive
                </button>
              ) : (
                <div className="space-y-4 p-4 bg-[#F9F6F0] rounded-lg">
                  <h4 className="font-semibold text-[#2A312C]">Informations Drive</h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-[#5C6A61] mb-1 block">Magasin *</label>
                      <input
                        type="text"
                        value={driveData.store_name}
                        onChange={(e) => setDriveData({ ...driveData, store_name: e.target.value })}
                        placeholder="Ex: Carrefour Drive Marseille"
                        className="input-default"
                        data-testid="drive-store-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-[#5C6A61] mb-1 block">Heure de récupération *</label>
                      <input
                        type="time"
                        value={driveData.pickup_time}
                        onChange={(e) => setDriveData({ ...driveData, pickup_time: e.target.value })}
                        className="input-default"
                        data-testid="drive-time-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-[#5C6A61] mb-1 block">Code / Nom de récupération *</label>
                      <input
                        type="text"
                        value={driveData.pickup_code}
                        onChange={(e) => setDriveData({ ...driveData, pickup_code: e.target.value })}
                        placeholder="Ex: DUPONT-123"
                        className="input-default"
                        data-testid="drive-code-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-[#5C6A61] mb-1 block">Notes (optionnel)</label>
                      <input
                        type="text"
                        value={driveData.notes}
                        onChange={(e) => setDriveData({ ...driveData, notes: e.target.value })}
                        placeholder="Informations complémentaires"
                        className="input-default"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-[#5C6A61]">
                    ⏰ Le drive doit être prêt avant le début du créneau. Le temps de récupération est inclus dans les 4h d'intervention.
                  </p>
                  <div className="flex gap-2">
                    <button 
                      onClick={handleSaveDrive}
                      disabled={saving}
                      className="btn-primary text-sm px-4 py-2"
                      data-testid="save-drive-btn"
                    >
                      {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                    <button 
                      onClick={() => setShowDriveForm(false)}
                      className="btn-outline text-sm px-4 py-2"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

    </div>
  );
}
